/**
 * ChainVault WP - Wallet Sound Effects & Voice Announcements
 *
 * Generates wallet sounds using the Web Audio API and
 * voice announcements using the Web Speech API.
 * No external audio files or libraries needed.
 *
 * @package ChainVault
 * @since   1.0.0
 */

let audioContext = null;

function getAudioContext() {
    if ( ! audioContext ) {
        audioContext = new ( window.AudioContext || window.webkitAudioContext )();
    }
    return audioContext;
}

/**
 * Resume the AudioContext if it was suspended
 * Call this before playing any sound after a long async operation
 */
export async function resumeAudio() {
    try {
        const ctx = getAudioContext();
        if ( ctx.state === 'suspended' ) {
            await ctx.resume();
        }
    } catch ( e ) { /* ignore */ }
}

/**
 * Speak a message out loud using the Web Speech API
 */
function speak( message, pitch = 1, rate = 1 ) {
    try {
        if ( ! window.speechSynthesis ) return;

        window.speechSynthesis.cancel();

        const utterance  = new SpeechSynthesisUtterance( message );
        utterance.pitch  = pitch;
        utterance.rate   = rate;
        utterance.volume = 1;

        const voices = window.speechSynthesis.getVoices();
        const englishVoice = voices.find( v =>
            v.lang.startsWith( 'en' ) && v.name.toLowerCase().includes( 'male' )
        ) || voices.find( v =>
            v.lang.startsWith( 'en' )
        );

        if ( englishVoice ) {
            utterance.voice = englishVoice;
        }

        window.speechSynthesis.speak( utterance );

    } catch ( error ) {
        console.warn( 'Could not speak announcement:', error );
    }
}

/**
 * Play receive sound — three ascending chimes
 * Then speaks the receive announcement
 */
export function playReceiveSound() {
    try {
        const ctx  = getAudioContext();
        const time = ctx.currentTime;

        const notes = [ 523.25, 659.25, 783.99 ];

        notes.forEach( ( freq, i ) => {
            const oscillator = ctx.createOscillator();
            const gainNode   = ctx.createGain();

            oscillator.connect( gainNode );
            gainNode.connect( ctx.destination );

            oscillator.type = 'sine';
            oscillator.frequency.setValueAtTime( freq, time + i * 0.12 );

            gainNode.gain.setValueAtTime( 0, time + i * 0.12 );
            gainNode.gain.linearRampToValueAtTime( 0.3, time + i * 0.12 + 0.05 );
            gainNode.gain.exponentialRampToValueAtTime( 0.001, time + i * 0.12 + 0.4 );

            oscillator.start( time + i * 0.12 );
            oscillator.stop( time + i * 0.12 + 0.4 );
        } );

        setTimeout( () => {
            speak( 'Excuse me Boss, transaction received.', 0.9, 0.95 );
        }, 600 );

    } catch ( error ) {
        console.warn( 'Could not play receive sound:', error );
    }
}

/**
 * Play send sound — descending swoosh
 * Then speaks the send announcement
 */
export function playSendSound() {
    try {
        const ctx  = getAudioContext();
        const time = ctx.currentTime;

        const oscillator = ctx.createOscillator();
        const gainNode   = ctx.createGain();

        oscillator.connect( gainNode );
        gainNode.connect( ctx.destination );

        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime( 880, time );
        oscillator.frequency.exponentialRampToValueAtTime( 220, time + 0.3 );

        gainNode.gain.setValueAtTime( 0, time );
        gainNode.gain.linearRampToValueAtTime( 0.25, time + 0.05 );
        gainNode.gain.exponentialRampToValueAtTime( 0.001, time + 0.35 );

        oscillator.start( time );
        oscillator.stop( time + 0.35 );

        const oscillator2 = ctx.createOscillator();
        const gainNode2   = ctx.createGain();

        oscillator2.connect( gainNode2 );
        gainNode2.connect( ctx.destination );

        oscillator2.type = 'sine';
        oscillator2.frequency.setValueAtTime( 330, time + 0.2 );
        oscillator2.frequency.exponentialRampToValueAtTime( 220, time + 0.45 );

        gainNode2.gain.setValueAtTime( 0, time + 0.2 );
        gainNode2.gain.linearRampToValueAtTime( 0.2, time + 0.25 );
        gainNode2.gain.exponentialRampToValueAtTime( 0.001, time + 0.5 );

        oscillator2.start( time + 0.2 );
        oscillator2.stop( time + 0.5 );

        setTimeout( () => {
            speak( 'Excuse me Boss, transaction sent.', 0.9, 0.95 );
        }, 600 );

    } catch ( error ) {
        console.warn( 'Could not play send sound:', error );
    }
}

/**
 * Play error sound — low buzz
 * Then speaks the error announcement
 */
export function playErrorSound() {
    try {
        const ctx  = getAudioContext();
        const time = ctx.currentTime;

        const oscillator = ctx.createOscillator();
        const gainNode   = ctx.createGain();

        oscillator.connect( gainNode );
        gainNode.connect( ctx.destination );

        oscillator.type = 'sawtooth';
        oscillator.frequency.setValueAtTime( 150, time );
        oscillator.frequency.setValueAtTime( 120, time + 0.1 );

        gainNode.gain.setValueAtTime( 0.2, time );
        gainNode.gain.exponentialRampToValueAtTime( 0.001, time + 0.3 );

        oscillator.start( time );
        oscillator.stop( time + 0.3 );

        setTimeout( () => {
            speak( 'Excuse me Boss, transaction failed.', 0.8, 0.9 );
        }, 400 );

    } catch ( error ) {
        console.warn( 'Could not play error sound:', error );
    }
}

/**
 * Preload voices so they are ready when needed
 */
export function preloadVoices() {
    try {
        if ( window.speechSynthesis ) {
            window.speechSynthesis.getVoices();

            if ( window.speechSynthesis.onvoiceschanged !== undefined ) {
                window.speechSynthesis.onvoiceschanged = () => {
                    window.speechSynthesis.getVoices();
                };
            }
        }
    } catch ( e ) { /* ignore */ }
}